import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticChiller } from "./realistic";

/**
 * ChillerNode – Chiller unit visualization.
 * Supports:
 * - Realistic 3D SCADA design with digital LCD temp display & refrigerant flow
 * - Glassmorphism design
 * - Classic industrial SVG layout
 */
const ChillerNode = (props) => {
  return (
    <BaseNode {...props}>
      {({ isRunning, isOffline, statusColor, speedScale, designType, color, opacity, liveValue, unit, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticChiller
              liveValue={liveValue}
              unit={unit || "°C"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              speedScale={speedScale}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }

        const strokeColor = isRunning ? color : "#94a3b8";
        const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
        const spinDuration = isRunning ? `${1.5 / (speedScale || 1)}s` : "0s";

        const showOldDesign = designType === "new_design" || designType === "classic";

        return (
          <svg
            width="80"
            height="80"
            viewBox="0 0 80 80"
            fill="none"
            style={{
              opacity,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            {!showOldDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                <defs>
                  <linearGradient id="chillerGlassGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                    <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
                  </linearGradient>
                  <linearGradient id="chillerActiveGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={color} stopOpacity="1" />
                    <stop offset="100%" stopColor={color} stopOpacity="0.5" />
                  </linearGradient>
                  <filter id="chillerGlow" x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.5" result="blur" />
                    <feComposite in="SourceGraphic" in2="blur" operator="over" />
                  </filter>
                </defs>

                {/* Chiller housing */}
                <rect
                  x="5"
                  y="8"
                  width="70"
                  height="50"
                  rx="8"
                  fill="url(#chillerGlassGrad)"
                  stroke={isRunning ? "url(#chillerActiveGrad)" : "#475569"}
                  strokeWidth="2.5"
                  style={{
                    filter: isRunning ? "url(#chillerGlow)" : "none",
                    transition: "all 0.3s ease",
                  }}
                />

                {/* Cooling coil (serpentine base) */}
                <path
                  d="M20 20 Q30 15 30 25 Q30 35 40 30 Q50 25 50 35 Q50 45 60 40"
                  stroke={color}
                  strokeWidth="5"
                  fill="none"
                  strokeLinecap="round"
                  opacity="0.3"
                />
                {/* Moving coolant pulse inside coil */}
                {isRunning && (
                  <path
                    d="M20 20 Q30 15 30 25 Q30 35 40 30 Q50 25 50 35 Q50 45 60 40"
                    stroke="#ffffff"
                    strokeWidth="3.2"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray="8 12"
                    filter="url(#chillerGlow)"
                  >
                    <animate attributeName="stroke-dashoffset" values="40;0" dur="1s" repeatCount="indefinite" />
                  </path>
                )}

                {/* Snowflakes / cold indicators */}
                {isRunning && (
                  <g filter="url(#chillerGlow)">
                    {/* Snowflake 1 */}
                    <g transform="translate(18, 38)" opacity="0.8">
                      <line x1="-5" y1="0" x2="5" y2="0" stroke={color} strokeWidth="2" />
                      <line x1="0" y1="-5" x2="0" y2="5" stroke={color} strokeWidth="2" />
                      <line x1="-3" y1="-3" x2="3" y2="3" stroke={color} strokeWidth="1" />
                      <line x1="3" y1="-3" x2="-3" y2="3" stroke={color} strokeWidth="1" />
                      <animateTransform attributeName="transform" type="rotate" values="0; 360" dur="5s" repeatCount="indefinite" />
                    </g>
                    {/* Snowflake 2 */}
                    <g transform="translate(60, 22)" opacity="0.6">
                      <line x1="-4" y1="0" x2="4" y2="0" stroke={color} strokeWidth="1.5" />
                      <line x1="0" y1="-4" x2="0" y2="4" stroke={color} strokeWidth="1.5" />
                      <animateTransform attributeName="transform" type="rotate" values="360; 0" dur="4s" repeatCount="indefinite" />
                    </g>
                  </g>
                )}

                {/* Fan at bottom */}
                <g style={{ transformOrigin: "60px 48px", animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none" }}>
                  <circle cx="60" cy="48" r="6" fill="url(#chillerGlassGrad)" stroke={strokeColor} strokeWidth="2" />
                  <line x1="55" y1="48" x2="65" y2="48" stroke={strokeColor} strokeWidth="1.5" />
                  <line x1="60" y1="43" x2="60" y2="53" stroke={strokeColor} strokeWidth="1.5" />
                </g>

                {/* Premium status dot - top right */}
                <circle cx="68" cy="14" r="5.5" fill={dotColor} stroke="#ffffff" strokeWidth="2" filter={isRunning ? "url(#chillerGlow)" : "none"}>
                  {isRunning && (
                    <animate attributeName="opacity" values="1;0.5;1" dur="1.2s" repeatCount="indefinite" />
                  )}
                </circle>
                <text x="40" y="73" textAnchor="middle" fontSize="7.5" fontWeight="800" fill={dotColor} style={{ letterSpacing: 0.5 }}>
                  {statusColor === "green" ? "COOLING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Chiller housing */}
                <rect x="5" y="8" width="70" height="50" rx="6" fill={`${color}10`} stroke={strokeColor} strokeWidth="2" />

                {/* Cooling coil (serpentine) */}
                <path
                  d="M20 20 Q30 15 30 25 Q30 35 40 30 Q50 25 50 35 Q50 45 60 40"
                  stroke={color}
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  style={{ animation: isRunning ? "live-chiller-pulse 2s ease-in-out infinite" : "none" }}
                />

                {/* Snowflake / cold indicator */}
                {isRunning && (
                  <g opacity="0.6" style={{ animation: "live-chiller-pulse 3s ease-in-out infinite" }}>
                    <line x1="15" y1="35" x2="25" y2="35" stroke={color} strokeWidth="1.5" />
                    <line x1="20" y1="30" x2="20" y2="40" stroke={color} strokeWidth="1.5" />
                    <line x1="17" y1="32" x2="23" y2="38" stroke={color} strokeWidth="1" />
                    <line x1="23" y1="32" x2="17" y2="38" stroke={color} strokeWidth="1" />
                  </g>
                )}

                {/* Fan at bottom */}
                <g style={{ transformOrigin: "60px 48px", animation: isRunning ? `live-pump-spin ${spinDuration} linear infinite` : "none" }}>
                  <circle cx="60" cy="48" r="5" fill="none" stroke={color} strokeWidth="1.5" />
                  <line x1="56" y1="48" x2="64" y2="48" stroke={color} strokeWidth="1" />
                  <line x1="60" y1="44" x2="60" y2="52" stroke={color} strokeWidth="1" />
                </g>

                {/* Pipes in/out */}
                <rect x="0" y="28" width="5" height="6" fill={color} rx="1" />
                <rect x="75" y="28" width="5" height="6" fill={color} rx="1" />

                {/* Status */}
                <circle cx="68" cy="14" r="4" fill={dotColor} />
                <text x="40" y="73" textAnchor="middle" fontSize="7" fontWeight="600" fill={dotColor}>
                  {statusColor === "green" ? "COOLING" : statusColor === "red" ? "STOPPED" : "OFFLINE"}
                </text>
              </>
            )}
          </svg>
        );
      }}
    </BaseNode>
  );
};

ChillerNode.propTypes = { data: PropTypes.object, selected: PropTypes.bool, id: PropTypes.string };
export default ChillerNode;
